/**
 * # How formatted values work
 *
 * 2 input elements are used.
 * 1. Is of `type="text"` which displays the formatted value.
 * 2. Is of `type="number"` for actual number input
 *
 * `onBlur` and `onFocus` events are used to swap between them.
 * - [`showInputField`] is called when 1 is focused. It shows (2) input field and focuses it.
 * - [`showFormattedValue`] is called when 2 is blurred (focus is lost). It shows (1) input field.
 */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {MatFormField} from '@angular/material/form-field';
import {MatTooltip} from '@angular/material/tooltip';
import {ComponentDefinition} from '@formbird/types';
import {ChangedDocumentService} from 'formbird-services';
import {FormbirdInjectorService} from '@services/injector/formbird-injector.service';
import * as numeral from 'numeral';
import {ComponentSetupService} from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import {AbstractComponent} from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import {Validators} from '@angular/forms';
import {NotificationService} from '@services/notification/notification.service';

interface NumericComponentDefinition extends ComponentDefinition {
  enabled: boolean;
  min?: number;
  max?: number;
  step?: number;
  integerValueOnly?: boolean;
  beforeStr?: string;
  afterStr?: string;
  format?: string;
  showLimitsText?: boolean;
  rightAlign?: boolean;
  defaultValue?: any;
  noZeroValue?: boolean;
  positiveOnly?: boolean;
  negativeOnly?: boolean;
}

@Component({
  templateUrl: './numeric.component.html',
  styleUrls: ['./numeric.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NumericComponent extends AbstractComponent<NumericComponentDefinition> implements OnInit, AfterViewInit {
  @ViewChild('input') inputElement!: ElementRef;
  @ViewChild('inputFormatted') inputFormatted!: ElementRef;
  formattedValue?: any;
  private integerValueOnly: boolean;
  private lastVal: any;
  private parser: any;
  shouldNotFloat: any;
  private requiredPatternIntegerOnly: RegExp;
  private requiredPatternPositiveOnly: RegExp;
  private requiredPatternNegativeOnly: RegExp;

  constructor(
    protected formbirdInjectorService: FormbirdInjectorService,
    protected cdRef: ChangeDetectorRef,
    protected changedDocumentService: ChangedDocumentService,
    protected componentSetupService: ComponentSetupService,
    private notificationService: NotificationService
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdRef);
  }

  ngOnInit() {
    super.ngOnInit();
    this.validators = [
      Validators.min(this.componentDefinition.min),
      Validators.max(this.componentDefinition.max),
    ];
    if (this.componentDefinition.integerValueOnly) {
      this.requiredPatternIntegerOnly =/^-?\d+$/;
      this.validators.push(Validators.pattern(this.requiredPatternIntegerOnly));
    }else if(this.componentDefinition.positiveOnly){
      this.requiredPatternPositiveOnly =/^\d*\.?\d+$/;
      this.validators.push(Validators.pattern(this.requiredPatternPositiveOnly));
    }else if(this.componentDefinition.negativeOnly){
      this.requiredPatternNegativeOnly =/^-\d*\.?\d+$/;
      this.validators.push(Validators.pattern(this.requiredPatternNegativeOnly));
    }
    this.formControl.setValidators(this.validators);
    this.formControl.updateValueAndValidity({emitEvent: false, onlySelf: true});
    const self = this;
    const compSub = this.changedDocumentService.watchComponentDef(this.fieldName, this.template.documentId).subscribe(tplItem => {
      self.onComponentDefChanged(tplItem);
      if (tplItem.enabled === false) {
        if (this.inputFormatted) {
          this.inputFormatted.nativeElement.disabled = true;
        }
        return;
      } else {
        if (this.inputFormatted) {
          this.inputFormatted.nativeElement.disabled = false;
        }
      }
    });
    this.subs.add(compSub);
  }

  protected initializeFieldValue() {
    this.parser = this.componentDefinition.integerValueOnly ? parseInt : parseFloat;
    if(!(this.fieldValue == null)){
      this.fieldValue = this.parser(this.fieldValue);
    }
    this.integerValueOnly = this.componentDefinition.integerValueOnly ? this.componentDefinition.integerValueOnly : false;
    this.lastVal = this.fieldValue;
    this.shouldNotFloat = false;
  }

  ngAfterViewInit(): void {
    if (this.componentDefinition.format !== undefined
       && (this.componentDefinition.defaultValue !== undefined
       || this.fieldValue !== undefined)) {
      this.formattedValue = numeral(this.fieldValue).format(this.componentDefinition.format);
      this.cdr.detectChanges();
    }
  }

  protected onControlValueChanged(value) {
    const hasError = (e) => this.formControl.hasError(e);
    let tempValue= value;
    if (hasError('min')) {
      const min = this.componentDefinition.min;
      this.formControl.setValue(min);
      tempValue = min;
      this.notificationService.warning(`Minimum value is (${min}). Reset numeric input-field to minimum value`);
    }

    if (hasError('max')) {
      const max = this.componentDefinition.max;
      this.formControl.setValue(max);
      tempValue = max;
      this.notificationService.warning(`Maximum value is (${max}). Reset numeric input-field to maximum value`);
    }
    if (hasError('pattern')) {
      const errorPattern = (this.formControl?.errors?.pattern?.requiredPattern).split('/').join('') ;
      if(this.requiredPatternIntegerOnly?.source === errorPattern){
        this.notificationService.warning('Only integers can be saved');
        tempValue =null;
        this.formControl.setValue(tempValue);
      }else if(this.requiredPatternPositiveOnly?.source === errorPattern){
        this.notificationService.warning('Only positive numbers can be saved');
        tempValue = value*(-1);
        this.formControl.setValue(tempValue);
      }else if(this.requiredPatternNegativeOnly?.source  === errorPattern){
        if(value===0){
          tempValue = null;
        }else{
          tempValue = value*(-1);
        }
        this.notificationService.warning('Only negative numbers can be saved');
        this.formControl.setValue(tempValue);
      }
    }
    this.valueChanged(tempValue);
  }

  /**
   * Shows the input field when the text box is focused.
   * This is used with format option, so we display the correct element and focus it.
   *
   * The correct element in this case is the actual input field to which the form control is attached.
   */
  showInputField(inputFormattedEl) {
    if (this.componentDefinition.format === undefined) {
      return;
    }
    if (this.componentDefinition.enabled === false) {
      inputFormattedEl.disabled = true;
      return;
    }else{
      inputFormattedEl.disabled = false;
    }
    this.formattedValue = null;
    requestAnimationFrame(() => this.inputElement.nativeElement.focus());
  }

  /**
   * Shows the formatted value when the focus is taken away from text box.
   * This is used with format option, so we display the correct element and focus it.
   *
   * The correct element in this case is the <input type="text" /> which displays the formatted value.
   * While a `<span>` or `<p>` may be a better choice for displaying formatted values, for visual consistency,
   * we use an `<input>`.
   */
  showFormattedValue(event?) {
    if(event){
      const comparePreviousFieldValueRegex = new RegExp(event.target.value,'gi');
      if(!comparePreviousFieldValueRegex.test(this.fieldValue)){
        this.fieldValue =  event.target.value;
      }
      if((new RegExp('^\s*$','gi')).test(event.target.value)){
        this.fieldValue = null;
      }
    }

    this.fieldValue = this.removeLeadingZeros(this.fieldValue) ;
    if (this.componentDefinition.format === undefined) {
      return;
    }
    if(!((this.fieldValue === null || this.fieldValue === undefined || this.fieldValue === ''))){
      this.formattedValue = numeral(this.fieldValue).format(this.componentDefinition.format);
      this.shouldNotFloat = false;
    }else{
      this.formattedValue = this.fieldValue;
      this.formControl.markAsTouched();
      this.shouldNotFloat = true;
    }

    this.cdr.detectChanges();
  }
  onFieldValueChanged(fieldValue) {
    this.lastVal = fieldValue;
    if (this.fieldValue === fieldValue) {
      return;
    }
    this.fieldValue = this.removeLeadingZeros(fieldValue) ;
    if (this.formControl) {
      this.formControl.setValue(this.fieldValue);
    }
    this.showFormattedValue();
  }
  removeLeadingZeros(fldVal){
    let retFieldValue = fldVal;
    if(this.componentDefinition.noZeroValue && ((fldVal !== undefined) && (fldVal !== null) && (fldVal !== ''))){
       let tempZeroVal = (fldVal+'').replace(new RegExp('^(-?)0+(.{1,100}\\d)$', 'gm'), `$1$2`);
      tempZeroVal = (tempZeroVal+'').replace(new RegExp('^(-?)0+([1-9]\\d*)$', 'gm'), `$1$2`);
      if((tempZeroVal+'').split('-.').length>1){
        tempZeroVal = '-0.'+(tempZeroVal+'').split('-.')[1];
      }
      if((tempZeroVal+'').split('.')[0].trim() ===''){
        tempZeroVal = '0.'+(tempZeroVal+'').split('.')[1];
      }
      if(parseFloat(tempZeroVal)===0){
        const noZeroWarnMessage = '\'You can not enter Zero. If you want a decimal use . without a zero\'';
        this.notificationService.warning(noZeroWarnMessage);
        console.warn(noZeroWarnMessage);
        retFieldValue = null;
      }else{
        retFieldValue = tempZeroVal;
      }
    }
    if (this.formControl) {
      this.formControl.setValue(retFieldValue);
    }
    return retFieldValue;
  }

  showToolTip(tooltip: MatTooltip, formFieldContainer: MatFormField, labelContainer: HTMLElement) {
    return (labelContainer.offsetWidth <= formFieldContainer._elementRef.nativeElement.scrollWidth);
  }

  toggleTooltip(tooltip: MatTooltip) {
    setTimeout(() => {
      tooltip.toggle();
    });
  }

  validateNumericInput(event) {
    if (event.inputType === 'insertText') {
      if (isNaN(event.data)) {
       if (event.data === '-'){
           if((this.lastVal === null || this.lastVal === undefined || this.lastVal === '')){
              // console.log('accept negative entry on first input', this.lastVal,event, event.data);
            }else if(((this.parser(this.lastVal)>0) )){
              this.fieldValue = this.parser('-'+this.lastVal);
              this.formControl.setValue(this.fieldValue);
            }else{
              this.fieldValue = this.lastVal;
              this.formControl.setValue(this.fieldValue);
            }
        }
      }
    }
  }

  focusNumericInput() {
    this.shouldNotFloat= false;
  }
}
